// ============================================================
// ELC Shift — Uploads / KPI module (v103.0)
//
// Area KPI scorecards — the admin-only KPI tab that aggregates
// sales/target/vs-LY per area (Dubai, Abu Dhabi, Qatar) across all
// stores in that area. Reads from the targets table (live v80+).
//
// Public surface:
//   • drawAreaKpiTable        — the main render (132 LOC)
//   • renderAreaKpis (async)  — outer orchestrator
//   • areaKpiScopeRange       — date scoping helper (WTD/MTD/YTD)
//
// Internal formatters (also exported for cohesion):
//   • _kpiFmtNum    — integer / abbreviated thousand display
//   • _kpiFmtPct    — percent display with sign
//   • _kpiPctColor  — green/red/grey CSS class by signed pct
//   • _kpiFmtCur    — currency display (AED/QAR aware)
//
// Free globals consumed:
//   DB, escapeHtml, formatDateShort
// ============================================================

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy

export function _kpiFmtNum(n) {
  if (n == null || !isFinite(n)) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(0) + 'K';
  return String(Math.round(n));
}

export function _kpiFmtPct(pct) {
  if (pct == null || !isFinite(pct)) return '—';
  const sign = pct >= 1 ? '' : '';
  return Math.round(pct * 100) + '%';
}

export function _kpiPctColor(pct) {
  if (pct == null) return 'var(--sub)';
  if (pct >= 1.0) return 'var(--ok)';
  if (pct >= 0.8) return 'var(--adm-yellow-text)';
  return 'var(--adm-red-text)';
}

export function _kpiFmtCur(n) {
  if (n == null || !isFinite(n) || n === 0) return '—';
  // Comma-separated, no currency symbol — meeting view leaves currency
  // implicit (UAE/Qatar mixed, both AED/QAR-ish, deck context makes it clear).
  return Math.round(n).toLocaleString('en-US');
}

export function areaKpiScopeRange(scope) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const fmt = d => d.toISOString().slice(0, 10);
  // v86.13: upper bound for "X to date" scopes is YESTERDAY (last
  // completed day). See dates.js effectiveToday() for rationale.
  // `fmt(now)` retained for past-date computations (last_week bounds).
  const effToday = effectiveToday();
  // Mon-of-this-week (ISO week start). JS getDay: 0=Sun..6=Sat → shift Mon=0.
  const dowMonZero = (now.getDay() + 6) % 7;
  const monThis = new Date(now); monThis.setDate(now.getDate() - dowMonZero);
  if (scope === 'this_week') return { from: fmt(monThis), to: effToday };
  if (scope === 'last_week') {
    const monLast = new Date(monThis); monLast.setDate(monThis.getDate() - 7);
    const sunLast = new Date(monThis); sunLast.setDate(monThis.getDate() - 1);
    return { from: fmt(monLast), to: fmt(sunLast) };
  }
  if (scope === 'QTD') {
    const qStart = Math.floor(m / 3) * 3;
    return { from: y + '-' + String(qStart + 1).padStart(2, '0') + '-01', to: effToday };
  }
  if (scope === 'YTD') return { from: y + '-01-01', to: effToday };
  // v131.14 (an area manager, 1 Sep: "Last month can not be seen on the Admin"):
  // whole calendar months, built by y/m arithmetic — never toISOString.
  const pad2 = n => String(n).padStart(2, '0');
  if (scope === 'last_month') {
    const end = new Date(y, m, 0);   // day 0 of this month = last day of the previous
    const ym2 = end.getFullYear() + '-' + pad2(end.getMonth() + 1);
    return { from: ym2 + '-01', to: ym2 + '-' + pad2(end.getDate()) };
  }
  if (scope === 'month' && typeof _areaKpiMonth !== 'undefined' && /^\d{4}-\d{2}$/.test(_areaKpiMonth || '')) {
    const [py, pm] = _areaKpiMonth.split('-').map(Number);
    let toStr = _areaKpiMonth + '-' + pad2(new Date(py, pm, 0).getDate());
    // The current month runs only to the last completed day, like MTD.
    if (_areaKpiMonth === effToday.slice(0, 7)) toStr = effToday;
    return { from: _areaKpiMonth + '-01', to: toStr };
  }
  // MTD default
  return { from: y + '-' + String(m + 1).padStart(2, '0') + '-01', to: effToday };
}

// v131.14: the any-month picker (admin.html <input type="month">). The picked
// value lives in _areaKpiMonth (declared in admin.html — adminPanels' scope
// buttons clear it, this bundle sets it: cross-bundle state stays in the HTML).
export function setAreaKpiMonth(val) {
  if (!/^\d{4}-\d{2}$/.test(val || '')) return;
  _areaKpiMonth = val;
  _areaKpiScope = 'month';
  document.querySelectorAll('#areaKpiScopeTabs .btn').forEach(b => {
    b.classList.remove('btn-green'); b.classList.add('btn-outline');
  });
  renderAreaKpis();
}

export function drawAreaKpiTable() {
  if (!_areaKpiData) return;
  const tableEl = document.getElementById('areaKpiTable');
  const areas = _areaKpiData.areas || [];
  const company = _areaKpiData.company || null;

  // v80.16 diagnostic — flat JSON so Chrome shows actual values, not "Array(N)".
  // Once drill-down is confirmed working, this log can be deleted.
  console.log('[areaKpis] render. expanded=' + JSON.stringify(Array.from(_areaKpiExpanded)) +
    ' areas=' + JSON.stringify(areas.map(a => ({
      id: a.area_id,
      idType: typeof a.area_id,
      storesLen: Array.isArray(a.stores) ? a.stores.length : 'NOT_ARRAY',
      hasMatch: _areaKpiExpanded.has(Number(a.area_id)),
    }))));

  // v80.16 patch — show actual / target / % stacked so area managers can
  // compare achievement rates at a glance, not just absolute counts.
  // Format: "58/148" on top, "39%" below, both color-coded by threshold.
  const addonCell = (actual, target) => {
    if (!target && !actual) return '<span style="color:var(--sub)">—</span>';
    if (!target) {
      return '<span style="color:var(--sub)">' + _kpiFmtNum(actual) + '/—</span>';
    }
    const pct = actual / target;
    const color = _kpiPctColor(pct);
    return '<div style="line-height:1.2;color:' + color + '">' +
             '<div style="font-weight:600">' + _kpiFmtNum(actual) + '/' + _kpiFmtNum(target) + '</div>' +
             '<div style="font-size:11px;font-weight:700">' + _kpiFmtPct(pct) + '</div>' +
           '</div>';
  };

  const rowHtml = (a, isCompanyTotal) => {
    const salesPct = a.target > 0 ? a.sales / a.target : null;
    const pctColor = _kpiPctColor(salesPct);
    // Coerce to Number — click handler stores numbers in the Set, but
    // a.area_id from JSON could conceivably arrive as a string. Strict
    // equality in Set.has() would silently fail. One coerce on each side
    // and the bug class is eliminated.
    const isExpanded = !isCompanyTotal && _areaKpiExpanded.has(Number(a.area_id));
    const expandIcon = isCompanyTotal ? '' : (isExpanded ? '▼ ' : '▶ ');
    const rowBg = isCompanyTotal ? 'background:var(--adm-ok-pale);font-weight:700' : '';
    // v80.16 final-final — no inline onclick at all. Render a button with
    // a class + data attribute, then bind real DOM listeners after innerHTML
    // (down at the bottom of drawAreaKpiTable). This is the only approach
    // that doesn't depend on global function visibility, inline-handler
    // CSP rules, or event delegation propagation. If THIS doesn't work,
    // the click event isn't reaching the button at all.
    const nameCell = isCompanyTotal
      ? escapeHtml(a.name)
      : '<button type="button" class="js-area-expand" data-area-id="' + a.area_id + '" style="background:none;border:none;padding:0;font:inherit;color:inherit;cursor:pointer;text-align:start;font-weight:600;text-decoration:underline dotted;text-underline-offset:3px">' +
          expandIcon + '<span translate="no">' + escapeHtml(a.name) + '</span>' +
        '</button>';
    return '<tr style="' + rowBg + '">' +
      '<td style="padding:10px 12px;border-bottom:1px solid var(--border);font-weight:' + (isCompanyTotal ? '700' : '600') + '">' +
        nameCell +
        (a.store_count != null ? ' <span style="color:var(--sub);font-weight:400;font-size:11px">(' + a.store_count + ' store' + (a.store_count === 1 ? '' : 's') + ')</span>' : '') +
      '</td>' +
      '<td style="padding:10px 12px;text-align:end;font-variant-numeric:tabular-nums;border-bottom:1px solid var(--border)">' + _kpiFmtCur(a.sales) + '</td>' +
      '<td style="padding:10px 12px;text-align:end;font-variant-numeric:tabular-nums;color:var(--sub);border-bottom:1px solid var(--border)">' + _kpiFmtCur(a.target) + '</td>' +
      '<td style="padding:10px 12px;text-align:end;font-variant-numeric:tabular-nums;font-weight:700;color:' + pctColor + ';border-bottom:1px solid var(--border)">' + _kpiFmtPct(salesPct) + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:12px;border-bottom:1px solid var(--border)">' + addonCell(a.actual_demo_units,    a.target_demo_units)    + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:12px;border-bottom:1px solid var(--border)">' + addonCell(a.actual_batteries,     a.target_batteries)     + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:12px;border-bottom:1px solid var(--border)">' + addonCell(a.actual_gift_wrap,     a.target_gift_wrap)     + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:12px;border-bottom:1px solid var(--border)">' + addonCell(a.actual_reusable_bag,  a.target_reusable_bag)  + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:12px;border-bottom:1px solid var(--border)">' + addonCell(a.actual_gift_cards,    a.target_gift_cards)    + '</td>' +
      '</tr>';
  };

  // Per-store sub-row (lighter background, indented label).
  const storeRowHtml = (s) => {
    const salesPct = s.target > 0 ? s.sales / s.target : null;
    const pctColor = _kpiPctColor(salesPct);
    return '<tr style="background:var(--surface-2)">' +
      '<td style="padding:8px 12px 8px 36px;border-bottom:1px solid var(--border);font-size:12px;color:var(--text)">' +
        '<span translate="no">' + escapeHtml(s.name) + '</span>' +
      '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;border-bottom:1px solid var(--border);font-size:12px">' + _kpiFmtCur(s.sales) + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;color:var(--sub);border-bottom:1px solid var(--border);font-size:12px">' + _kpiFmtCur(s.target) + '</td>' +
      '<td style="padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;font-weight:600;color:' + pctColor + ';border-bottom:1px solid var(--border);font-size:12px">' + _kpiFmtPct(salesPct) + '</td>' +
      '<td style="padding:6px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:11px;border-bottom:1px solid var(--border)">' + addonCell(s.actual_demo_units,    s.target_demo_units)    + '</td>' +
      '<td style="padding:6px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:11px;border-bottom:1px solid var(--border)">' + addonCell(s.actual_batteries,     s.target_batteries)     + '</td>' +
      '<td style="padding:6px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:11px;border-bottom:1px solid var(--border)">' + addonCell(s.actual_gift_wrap,     s.target_gift_wrap)     + '</td>' +
      '<td style="padding:6px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:11px;border-bottom:1px solid var(--border)">' + addonCell(s.actual_reusable_bag,  s.target_reusable_bag)  + '</td>' +
      '<td style="padding:6px 12px;text-align:end;font-variant-numeric:tabular-nums;font-size:11px;border-bottom:1px solid var(--border)">' + addonCell(s.actual_gift_cards,    s.target_gift_cards)    + '</td>' +
      '</tr>';
  };

  let html = '<table class="data-table" style="font-size:13px"><thead><tr>' +
    '<th style="text-align:start">Area</th>' +
    '<th style="text-align:end">Sales</th>' +
    '<th style="text-align:end">Target</th>' +
    '<th style="text-align:end">vs Target</th>' +
    '<th style="text-align:end">Demo</th>' +
    '<th style="text-align:end">Bat</th>' +
    '<th style="text-align:end">Wrap</th>' +
    '<th style="text-align:end">Bag</th>' +
    '<th style="text-align:end">Cards</th>' +
    '</tr></thead><tbody>';

  areas.forEach(a => {
    html += rowHtml(a, false);
    if (_areaKpiExpanded.has(Number(a.area_id)) && Array.isArray(a.stores)) {
      // Sort stores by sales descending so the biggest contributors are top.
      const sortedStores = a.stores.slice().sort((x, y) => (y.sales || 0) - (x.sales || 0));
      sortedStores.forEach(s => { html += storeRowHtml(s); });
    }
  });
  if (company) html += rowHtml(company, true);
  html += '</tbody></table>';

  tableEl.innerHTML = html;

  // Wire click handlers via direct addEventListener on each button.
  // No reliance on inline onclick / global function visibility / event
  // delegation. This is the most reliable mechanism JavaScript provides
  // and any failure means the click event isn't reaching the button.
  const buttons = tableEl.querySelectorAll('button.js-area-expand');
  console.log('[areaKpis] wired', buttons.length, 'expand buttons');
  buttons.forEach(function(btn){
    btn.addEventListener('click', function(e){
      e.preventDefault();
      e.stopPropagation();
      const areaId = Number(btn.getAttribute('data-area-id'));
      console.log('[areaKpis] expand clicked, areaId =', areaId);
      if (!isFinite(areaId)) return;
      if (_areaKpiExpanded.has(areaId)) _areaKpiExpanded.delete(areaId);
      else _areaKpiExpanded.add(areaId);
      drawAreaKpiTable();
    });
  });
}

// v131.14: which slice the daily table shows ('company' | 'country:X' |
// 'area:N'). Written only by this bundle, so it may live module-level.
let _areaKpiDailyScope = 'company';

// v131.14: the per-day sales-vs-target table under the roll-up (an area manager,
// 1 Sep: "I can not see daily target for the region and area"). Four columns
// so it fits a 360px Android screen; scope picked via the select above it.
export function drawAreaKpiDaily() {
  const wrap = document.getElementById('areaKpiDaily');
  const sel  = document.getElementById('areaKpiDailyScope');
  if (!wrap || !sel) return;
  const daily = (_areaKpiData && _areaKpiData.daily) || [];
  if (!daily.length) {
    sel.style.display = 'none';
    wrap.innerHTML = '<div style="padding:24px;text-align:center;color:var(--sub);font-size:13px">No daily sales or targets in this period yet.</div>';
    return;
  }
  sel.style.display = '';
  // Rebuild the scope options from the data, preserving the current choice.
  const areaOpts = {}, countryOpts = {};
  daily.forEach(d => {
    (d.areas || []).forEach(a => { areaOpts[a.area_id] = a.name; });
    (d.countries || []).forEach(c => { countryOpts[c.country] = true; });
  });
  let opts = '<option value="company">Company</option>';
  Object.keys(countryOpts).sort().forEach(c => {
    opts += '<option value="country:' + escapeHtml(c) + '">' + escapeHtml(c) + ' (country)</option>';
  });
  Object.keys(areaOpts).map(Number).sort((a, b) => a - b).forEach(id => {
    opts += '<option value="area:' + id + '">' + escapeHtml(areaOpts[id]) + '</option>';
  });
  sel.innerHTML = opts;
  sel.value = _areaKpiDailyScope;
  if (!sel.value) { sel.value = 'company'; _areaKpiDailyScope = 'company'; }
  const pick = (d) => {
    const v = _areaKpiDailyScope;
    if (v.indexOf('country:') === 0) return (d.countries || []).find(c => c.country === v.slice(8)) || { sales: 0, target: 0 };
    if (v.indexOf('area:') === 0)    return (d.areas || []).find(a => a.area_id === Number(v.slice(5))) || { sales: 0, target: 0 };
    return d.company || { sales: 0, target: 0 };
  };
  const td = 'padding:8px 12px;text-align:end;font-variant-numeric:tabular-nums;border-bottom:1px solid var(--border);font-size:13px';
  let totSales = 0, totTarget = 0, rows = '';
  daily.forEach(d => {
    const b = pick(d);
    totSales += b.sales; totTarget += b.target;
    const pct = b.target > 0 ? b.sales / b.target : null;
    const dow = new Date(d.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
    rows += '<tr>' +
      '<td style="padding:8px 12px;border-bottom:1px solid var(--border);font-size:13px;white-space:nowrap"><span>' + escapeHtml(dow) + '</span> ' + escapeHtml(d.date.slice(8)) + '</td>' +   // v135.2: the day name on its own, so Arabic can swap it
      '<td style="' + td + ';color:var(--sub)">' + _kpiFmtCur(b.target) + '</td>' +
      '<td style="' + td + '">' + _kpiFmtCur(b.sales) + '</td>' +
      '<td style="' + td + ';font-weight:700;color:' + _kpiPctColor(pct) + '">' + _kpiFmtPct(pct) + '</td>' +
      '</tr>';
  });
  const totPct = totTarget > 0 ? totSales / totTarget : null;
  rows += '<tr style="background:var(--adm-ok-pale);font-weight:700">' +
    '<td style="padding:10px 12px;font-size:13px">Total</td>' +
    '<td style="' + td + '">' + _kpiFmtCur(totTarget) + '</td>' +
    '<td style="' + td + '">' + _kpiFmtCur(totSales) + '</td>' +
    '<td style="' + td + ';color:' + _kpiPctColor(totPct) + '">' + _kpiFmtPct(totPct) + '</td>' +
    '</tr>';
  wrap.innerHTML = '<table class="data-table" style="font-size:13px;width:100%"><thead><tr>' +
    '<th style="text-align:start">Day</th>' +
    '<th style="text-align:end">Target</th>' +
    '<th style="text-align:end">Sales</th>' +
    '<th style="text-align:end">vs Target</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table>';
}

// v131.14: select onchange handler — remember the choice, redraw locally.
export function setAreaKpiDailyScope(val) {
  _areaKpiDailyScope = val || 'company';
  drawAreaKpiDaily();
}

export async function renderAreaKpis() {
  const tableEl = document.getElementById('areaKpiTable');
  const labelEl = document.getElementById('areaKpiPeriodLabel');
  if (!tableEl || !labelEl) return;   // v136.8: null-safe (the box may not be on screen)
  const range = areaKpiScopeRange(_areaKpiScope);
  labelEl.textContent = 'Period: ' + range.from + ' → ' + range.to;
  tableEl.innerHTML = '<div style="padding:30px;text-align:center;color:var(--sub);font-size:13px">Loading…</div>';
  try {
    // v127.28: endpoint is admin-gated now — send the session token (same
    // pattern as compliance.js).
    // v131.14: daily=1 — the per-day series rides the same call (the server
    // builds it from rows it already fetched, so this costs no extra query).
    const res = await fetch(DB.workerUrl() + '/api/area_kpis?from=' + range.from + '&to=' + range.to + '&daily=1', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + adminToken() },   // v136.12: the shared read (DB token first), not sessionStorage alone
    });
    if (!res.ok) throw new Error('fetch failed: ' + res.status);
    _areaKpiData = await res.json();
    drawAreaKpiTable();
    drawAreaKpiDaily();
  } catch (e) {
    tableEl.innerHTML = '<div style="padding:30px;text-align:center;color:var(--adm-red-text);font-size:13px">Could not load Area KPIs: ' + escapeHtml(e.message) + '</div>';
  }
}

